import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Base from '../Base';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import { composeBadge } from '../../components/images/composeBadge';

import '../user/PublicProfile.css';
import '../event/EventList.css';
import './Badge.css';

const Page = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | nophoto | error
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [sharing, setSharing] = useState(false);

  // Fetch the badge payload (name/team/event + photo & frame as data URIs).
  useEffect(() => {
    let cancelled = false;
    apiRequest('GET', '/my-badge')
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, []);

  // Composite the PNG once the payload is in.
  useEffect(() => {
    if (!data) return undefined;
    if (!data.has_photo) { setStatus('nophoto'); return undefined; }
    if (!data.photo_data_uri) { setStatus('error'); return undefined; }

    let cancelled = false;
    let url = null;
    setStatus('loading');
    composeBadge({
      photoDataUri: data.photo_data_uri,
      frameDataUri: data.frame_data_uri,
      logoDataUri: data.logo_data_uri,
      fullName: data.full_name,
      teamName: data.team_name,
      eventName: data.event_name,
      eventDate: data.event_date,
      primaryColor: data.primary_color,
      secondaryColor: data.secondary_color,
      lang: i18n.language,
    })
      .then((b) => {
        if (cancelled) return;
        url = URL.createObjectURL(b);
        setBlob(b);
        setPreviewUrl(url);
        setStatus('ready');
      })
      .catch(() => { if (!cancelled) setStatus('error'); });

    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [data, i18n.language]);

  const fileName = useMemo(() => {
    const base = (data?.event_name || 'badge').replace(/[^\w-]+/g, '_').slice(0, 40) || 'badge';
    return `${base}.png`;
  }, [data]);

  const handleDownload = () => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const handleShare = async () => {
    if (!blob) return;
    const file = new File([blob], fileName, { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        setSharing(true);
        await navigator.share({
          files: [file],
          title: t('badgeShareTitle'),
          text: t('badgeShareText', { event: data?.event_name || '' }),
        });
        return;
      } catch (e) {
        // cancelled or failed — fall through to download
      } finally {
        setSharing(false);
      }
    }
    handleDownload();
  };

  return (
    <Base activeNav="home">
      <div className="pp-page">
        <div className="el-topbar">
          <button type="button" className="el-back-btn" onClick={() => navigate(-1)} aria-label="Back">
            <Icon name="angle-left" />
          </button>
          <h1 className="el-page-title">{t('badgeTitle')}</h1>
        </div>

        {status === 'nophoto' ? (
          <div className="badge-prompt">
            <div className="badge-prompt__icon"><Icon name="user" /></div>
            <h2 className="badge-prompt__title">{t('badgeAddPhotoTitle')}</h2>
            <Link to="/edit-profile" className="badge-btn badge-btn--primary">
              <Icon name="picture" pack="sr" /> {t('badgeAddPhotoCta')}
            </Link>
          </div>
        ) : status === 'error' ? (
          <div className="pp-empty">
            <div className="pp-empty-icon"><Icon name="info" /></div>
            <div className="pp-empty-title">{t('badgeError')}</div>
          </div>
        ) : (status === 'loading' || !previewUrl) ? (
          <div className="badge-loading">
            <div className="badge-skel" />
            <p className="badge-loading__text">{t('badgeGenerating')}</p>
          </div>
        ) : (
          <>
            <div className="badge-preview">
              <img src={previewUrl} alt="" className="badge-preview__img" />
            </div>
            <div className="badge-actions">
              <button type="button" className="badge-btn badge-btn--primary" onClick={handleShare} disabled={sharing}>
                <Icon name="share-square" /> {t('share')}
              </button>
              <button type="button" className="badge-btn badge-btn--ghost" onClick={handleDownload}>
                <Icon name="download" /> {t('badgeDownload')}
              </button>
            </div>
            <p className="badge-hint">{t('badgeSaveHint')}</p>
          </>
        )}
      </div>
    </Base>
  );
};

export default Page;
